import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { AvTag, AvTooltip, Illustration } from '@fabric-ui/components';
import { ArrowDown, Forward, History } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Box, Button } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import * as d3 from 'd3';
import { formatTimeRange } from '../../views/CustomDashboards/components/TimeFilter/utils';

const historyIcon = <History strokeWidth={0.5} style={{ height: 16, width: 16 }} />;

interface WidgetTitleProps {
  children: React.ReactNode;
  marginBottom: number;
}
export const WidgetTitle: React.FC<WidgetTitleProps> = ({ children, marginBottom }) => (
  <Box sx={{ marginBottom, fontSize: 18, fontWeight: 600 }}>{children}</Box>
);
interface WidgetDescriptionProps {
  showImage?: boolean;
  title?: ReactNode;
  description?: ReactNode;
  icon?: React.ReactNode;
  svgProps?: { style?: React.CSSProperties };
}
const HIDE_TEXT_HEIGHT_THRESHOLD = 129;

export const NoDataFoundWidget: React.FC<WidgetDescriptionProps> = ({
  showImage = true,
  title = 'No Data Found',
  description = '',
  icon,
  svgProps,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [hideText, setHideText] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new ResizeObserver(([entry]) => setHideText(entry.contentRect.height <= HIDE_TEXT_HEIGHT_THRESHOLD));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <Box ref={ref} sx={{ ...flex.colCenter, textAlign: 'center', flex: 1, svg: { width: 'auto', height: 'auto' } }}>
      {showImage &&
        (icon || (
          <Box
            sx={{
              width: '12%',
              maxWidth: 72,
              aspectRatio: '1',
              ...svgProps?.style,
              '& svg': { width: '100%', height: '100%' },
            }}>
            <Illustration.Small.NoData />
          </Box>
        ))}
      {!hideText && (
        <>
          <Typography variant="h5" sx={{ color: 'var(--color-content-base-tertiary)', pt: 3, pb: 2 }}>
            {title}
          </Typography>
          <div>{description}</div>
        </>
      )}
    </Box>
  );
};

interface ErrorLoadingWidgetProps {
  text?: string;
  onClick?: () => void;
}
export const ErrorLoadingWidget: React.FC<ErrorLoadingWidgetProps> = ({ text = 'Try to reload the page', onClick }) => {
  const content =
    typeof onClick === 'function' ? (
      <Button sx={{ color: theme => theme.palette.primary.main, fontWeight: 600 }} variant="text" onClick={onClick}>
        {text}
      </Button>
    ) : (
      <div>{text}</div>
    );
  return (
    <Box sx={{ ...flex.colCenter, textAlign: 'center', flex: 1, svg: { width: 'auto', height: 'auto' } }}>
      <Box sx={{ width: '12%', maxWidth: 72, aspectRatio: '1', '& svg': { width: '100%', height: '100%' } }}>
        <Illustration.Small.Issue />
      </Box>
      <Typography variant="h5" sx={{ color: 'var(--color-content-base-tertiary)', pt: 3, pb: 2 }}>
        Error Fetching Data
      </Typography>
      {content}
    </Box>
  );
};

const actionStyle = theme => ({
  ...flex.itemsCenter,
  gap: 1,
  fontWeight: 600,
  color: theme.palette.primary.main,
});

interface ExploreLinkProps {
  actionHref: string;
  title: string;
  sx?: React.CSSProperties;
}
export const ExploreLink: React.FC<ExploreLinkProps> = ({ actionHref, title, sx = {} }) => (
  <Link to={actionHref}>
    <Box sx={theme => ({ ...actionStyle(theme), ...sx })}>
      Explore {title} <Forward width={20} />
    </Box>
  </Link>
);

export const tickFormat = d3.format('.2~s');

export const getHorizontalWidth = data =>
  100 -
  Math.min(
    80,
    Math.max(
      20,
      100 -
        data
          .map(datum => datum.name)
          .reduce((max, currentString?: string) => (currentString?.length && currentString.length > max ? currentString.length : max), 0) *
          5
    )
  );

export function HistoricDataIndicator({
  hideText,
  title = 'Historical Data',
  variant,
  sx = {},
}: {
  hideText?: boolean;
  title?: React.ReactNode;
  variant?: React.ComponentProps<typeof AvTag>['variant'];
  sx?;
}) {
  return (
    <AvTooltip title={hideText ? title : ''}>
      <div>
        <AvTag
          variant={variant}
          text={
            <>
              {historyIcon}
              {!hideText && title}
            </>
          }
          sx={{ ...flex.itemsCenter, gap: '6px', ...sx }}
        />
      </div>
    </AvTooltip>
  );
}

export const HistoricalIndicationWithTimeRange = ({ timeRange }) => (
  <AvTooltip
    title="Displayed values are from the start or end of the selected time range (depending on the measurement) and might differ from the current value."
    popperProps={{ sx: { '.MuiTooltip-tooltip': { width: '328px', maxWidth: 'none' } } }}>
    <Box sx={{ ...flex.itemsCenter }}>
      <AvTag
        sx={{
          borderTopRightRadius: '0px',
          borderBottomRightRadius: '0px',
          borderRight: 'none',
          fontSize: '12px',
          textWrapMode: 'nowrap',
        }}
        text={formatTimeRange(timeRange, 'dd/MM/yy')}
      />

      <HistoricDataIndicator
        hideText
        title=""
        variant="primary"
        sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, padding: '3px' }}
      />
    </Box>
  </AvTooltip>
);

export const ArrowDownIcon = isCollapse => (
  <ArrowDown style={{ ...iconSize(16), transform: `rotate(${isCollapse ? 0 : -90}deg)`, color: 'var(--color-content-base-secondary)' }} />
);
